import React, { useState, useEffect } from 'react'
import { supabase } from './utils/supabaseClient'
import Zemelapis from './components/Zemelapis'
import Filtrai from './components/Filtrai'
import VietaDetails from './pages/VietaDetails'
import useLocation from './hooks/useLocation'
import Onboarding from './components/Onboarding'
import Splash from './components/Splash'
import Rulete from './pages/Rulete'
import './styles/main.css'

function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLng = ((lng2 - lng1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2)
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function BottomSheet({ busena, onBusenaChange, onUzdaryti, children }) {
  const pradžiaY = React.useRef(null)
  const sheetRef = React.useRef(null)

  function onTouchStart(e) {
    pradžiaY.current = e.touches[0].clientY
  }

  function onTouchEnd(e) {
    if (pradžiaY.current === null) return
    const delta = e.changedTouches[0].clientY - pradžiaY.current
    pradžiaY.current = null

    if (delta < -50) {
      // Braukta aukštyn
      if (busena === 'peek') onBusenaChange('half')
      else if (busena === 'half') onBusenaChange('full')
    } else if (delta > 50) {
      // Braukta žemyn
      if (busena === 'full') onBusenaChange('half')
      else if (busena === 'half') onBusenaChange('peek')
      else if (busena === 'peek') onUzdaryti()
    }
  }

  // Pelės palaikymui (desktop testavimui)
  function onMouseDown(e) {
    pradžiaY.current = e.clientY
  }

  function onMouseUp(e) {
    if (pradžiaY.current === null) return
    const delta = e.clientY - pradžiaY.current
    pradžiaY.current = null

    if (delta < -30) {
      if (busena === 'peek') onBusenaChange('half')
      else if (busena === 'half') onBusenaChange('full')
    } else if (delta > 30) {
      if (busena === 'full') onBusenaChange('half')
      else if (busena === 'half') onBusenaChange('peek')
      else if (busena === 'peek') onUzdaryti()
    }
  }

  return (
    <div
      ref={sheetRef}
      className={`bottom-sheet ${busena}`}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="sheet-rankena" />
      {children}
    </div>
  )
}

function App() {
  const [visosVietos, setVisosVietos] = useState([])
  const [rodomos, setRodomos] = useState([])
  const [pasirinkta, setPasirinkta] = useState(null)
  const [aktyviosKategorijos, setAktyviosKategorijos] = useState([])
  const [sheetBusena, setSheetBusena] = useState('peek')
  const [issaugota, setIssaugota] = useState(false)
  const [splashMinBaigtas, setSplashMinBaigtas] = useState(false)
  const [duomenysUzkrauti, setDuomenysUzkrauti] = useState(false)
  const [onboardingBaigta, setOnboardingBaigta] = useState(
    () => !!localStorage.getItem('onboarding_baigta')
  )
  const { lokacija, klaida, kraunasi } = useLocation()

  function handleOnboardingBaigta() {
    localStorage.setItem('onboarding_baigta', 'true')
    setOnboardingBaigta(true)
  }

  useEffect(() => {
    const t = setTimeout(() => setSplashMinBaigtas(true), 600)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    async function fetchVietos() {
      const { data, error } = await supabase.from('vietos').select('*')
      if (error) console.error(error)
      else {
        setVisosVietos(data)
        setRodomos(data)
        setDuomenysUzkrauti(true)
      }
    }
    fetchVietos()
  }, [])

  function filtruoti({ kategorija, vaikams }) {
    let rezultatai = [...visosVietos]

    if (kategorija !== 'visi') {
      rezultatai = rezultatai.filter(v => v.kategorija === kategorija)
    }

    if (vaikams) {
      rezultatai = rezultatai.filter(v => v.tinka_vaikams === true)
    }

    setRodomos(rezultatai)
    setAktyviosKategorijos(kategorija !== 'visi' ? [kategorija] : [])
  }

  if (!splashMinBaigtas || !duomenysUzkrauti) return <Splash />
  if (!onboardingBaigta) return <Onboarding onBaigta={handleOnboardingBaigta} />
  return <Rulete />

  if (kraunasi) return (
    <div className="kraunasi">
      <p className="kraunasi-antraste">Ieškau įdomių vietų</p>
      <p className="kraunasi-meta">aplink tave · 30 km spinduliu</p>
      <div className="kraunasi-taskai">
        <span></span><span></span><span></span>
      </div>
    </div>
  )
  if (klaida) return <div className="klaida">{klaida}</div>

  return (
    <div className="app-zemelapis" onClick={() => { if (pasirinkta) setPasirinkta(null) }}>
      <Zemelapis
        vietos={rodomos}
        lokacija={lokacija}
        onVietaPasirinkta={(vieta) => { setPasirinkta(vieta); setSheetBusena('peek') }}
        pasirinkta={pasirinkta}
        aktyviosKategorijos={aktyviosKategorijos}
      />

      <Filtrai onFiltruoti={filtruoti} />

      {pasirinkta && (
        <BottomSheet
          busena={sheetBusena}
          onBusenaChange={setSheetBusena}
          onUzdaryti={() => setPasirinkta(null)}
        >
          <VietaDetails
            vieta={pasirinkta}
            busena={sheetBusena}
            issaugota={issaugota}
            onSave={(e) => { e.stopPropagation(); setIssaugota(!issaugota) }}
          />
        </BottomSheet>
      )}
    </div>
  )
}

export default App